import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// ============================================================
//  GLB-модели гранат — единый источник для ТРЁХ потребителей:
//    • вьюмодель в своей руке      (grenades.js, система камеры);
//    • летящая граната в мире       (grenades.js, система сцены);
//    • граната в руке чужого/бота/трупа (remote.js).
//
//  Ровно как у стволов есть вьюмодель (weapons.js) и мировая модель
//  (remote.js), но у гранат форма одна и та же во всех руках, поэтому шаблон
//  общий — плодить два набора незачем. Браузер отдаёт GLB из HTTP-кэша, так
//  что повторные запросы почти бесплатны.
//
//  Модели — из CC0-пака «Flat Grenades» (OpenGameArt), см. tools/grenade-pack/
//  SOURCE.txt и md/graphics.md. Тела у всех тёмно-серые, тип различает ПОЛОСА-
//  маркер (материал M_PCL_Flat_Red.Brick) — её и красим под тип. Это тот же
//  «предохранительный поясок», по которому тип опознают в реале.
//
//  ⚠️ Цвет полосы — это и есть «какая граната летит». Он обязан совпадать в
//  руке и в полёте: раз шаблон общий, совпадение бесплатно. Порядок массива =
//  индексу типа из net.js (0 frag, 1 smoke, 2 flash, 3 molotov) — НЕ менять.
// ============================================================

const NADE_MODEL = [
  { url: '/models/nade_frag.glb',    h: 0.115, band: 0x6e8b2a },  // осколочная — оливковая полоса
  { url: '/models/nade_smoke.glb',   h: 0.150, band: 0x8fa39a },  // дымовая — серо-зелёная
  { url: '/models/nade_flash.glb',   h: 0.150, band: 0xe6e2c8 },  // светошумовая — кремовая
  { url: '/models/nade_molotov.glb', h: 0.170, band: 0xd2691e },  // молотов — оранжевая (огонь)
];
/** Имя материала полосы-маркера в паке — его перекрашиваем под тип */
const BAND_MAT = 'M_PCL_Flat_Red.Brick';
/** Цвет полосы по индексу типа — читают grenades.js (превью-линия) и т.п. */
export const NADE_BAND = NADE_MODEL.map((m) => m.band);

const TYPE_IDX = { frag: 0, smoke: 1, flash: 2, molotov: 3 };
const idxOf = (t) => (typeof t === 'number' ? t : TYPE_IDX[t]);

const tpl = [null, null, null, null];      // готовые шаблоны (общие материалы), клонируются
const pending = [[], [], [], []];          // кого заменить, когда шаблон догрузится
const loader = new GLTFLoader();

/** Собирает шаблон: масштаб под целевую высоту, центрирование по bbox (граната
 *  лежит центром в кулаке — GRIP_NADE = [0,0,0] в remote.js), перекраска полосы
 *  под тип и лёгкое осветление почти чёрных частей, чтобы силуэт читался на
 *  тёмной карте. Возвращает группу, готовую к `.clone(true)`. */
function buildTemplate(idx, model) {
  const m = NADE_MODEL[idx];
  const sy = new THREE.Box3().setFromObject(model).getSize(new THREE.Vector3()).y || 1;
  model.scale.setScalar(m.h / sy);
  const c = new THREE.Box3().setFromObject(model).getCenter(new THREE.Vector3());
  model.position.set(-c.x, -c.y, -c.z);
  model.traverse((o) => {
    if (!o.isMesh) return;
    o.castShadow = true;
    o.material = o.material.clone();
    if (o.material.name === BAND_MAT) {
      o.material.color.setHex(m.band);
    } else {
      // тела пака почти чёрные (baseColor 0.03..0.21) — поднимаем «пол», иначе
      // граната в тени сливается в бесформенное пятно
      const col = o.material.color;
      col.setRGB(Math.max(col.r, 0.10), Math.max(col.g, 0.10), Math.max(col.b, 0.10));
    }
    o.material.metalness = 0.25;
    o.material.roughness = 0.7;
  });
  const g = new THREE.Group();
  g.add(model);
  return g;
}

NADE_MODEL.forEach((m, idx) => {
  loader.load(m.url, (gltf) => {
    try {
      tpl[idx] = buildTemplate(idx, gltf.scene);
      for (const cb of pending[idx]) cb(tpl[idx]);
      pending[idx].length = 0;
    } catch (e) { console.error('модель гранаты: сборка шаблона', idx, e); }
  }, undefined, (e) => console.error('модель гранаты: загрузка', idx, e));
});

/** Примитив-фолбэк (шарик), пока GLB не готов или если он не загрузился вовсе */
function fallback(idx) {
  return new THREE.Mesh(new THREE.SphereGeometry(0.06, 8, 8),
    new THREE.MeshStandardMaterial({ color: NADE_MODEL[idx].band, roughness: 0.6 }));
}

/**
 * Держатель одной гранаты. Пока GLB не готов — внутри шарик; как только шаблон
 * догрузится, у живого он заменяется на GLB (`swap`). Устроено ровно как
 * `makeWorldGun` в remote.js:
 *   own   — клонировать материалы (нужно трупу: он тает прозрачностью, и красить
 *           общий материал шаблона нельзя — растворились бы гранаты у всех).
 *           Клоны возвращаются в `mats` для списка растворения.
 *   ghost — материал-невидимка для СВОЕГО тела (body.js): иначе загрузившийся
 *           GLB всплыл бы поверх вьюмодели — баг-урок «два ствола/гранаты».
 *           Применяется и при асинхронной подмене примитива на GLB.
 *   swap  — досваживать примитив на GLB, когда шаблон догрузится (труп не
 *           досваживают: он строится на уже готовом шаблоне).
 *
 * Возвращает { group, mats } (mats — материалы для растворения, только при own).
 */
export function makeNade(type, { own = false, ghost = null, swap = true } = {}) {
  const idx = idxOf(type);
  const holder = new THREE.Group();
  const extra = [];
  const applyGhost = (root) => { if (ghost) root.traverse((o) => { if (o.isMesh) o.material = ghost; }); };
  const attach = (t) => {
    const inst = t.clone(true);
    // own (труп): личный клон материалов, заранее transparent — иначе труп
    // выставляет opacity, а без флага она игнорируется и граната не тает
    // вместе с телом (тот же приём, что в cloneMaterials, remote.js).
    if (own) inst.traverse((o) => {
      if (!o.isMesh) return;
      o.material = o.material.clone();
      o.material.transparent = true;
      extra.push(o.material);
    });
    holder.add(inst);
    applyGhost(inst);
  };
  if (tpl[idx]) {
    attach(tpl[idx]);
  } else {
    const p = fallback(idx);
    if (own) { p.material.transparent = true; extra.push(p.material); }
    holder.add(p);
    applyGhost(p);
    if (swap) pending[idx].push((t) => { holder.remove(p); attach(t); });
  }
  return { group: holder, mats: extra };
}
